import type {
  ViewPrincipalType,
  ViewRole,
} from '../../../__generated__/chirp/com/hubspot/sales/rpc/ViewsCrudRpc';

export const STANDARD = 'STANDARD' as const;
export const DEFAULT = 'DEFAULT' as const;
export const FORECAST = 'FORECAST' as const;

export const DESC = 1;
export const ASC = -1;

export type ViewIdType = string;
export type ViewNamespaceType = string;

export type ViewPrivateType = boolean;

export type ViewTeamIdType = number | undefined;

export type ViewUserIdsType = number[];

export type ViewPermissionDetailsType = { userIds: ViewUserIdsType };

// Roles and principal types are sourced from the generated CHIRP enums (the
// `unhandledEnumCase` symbol member is stripped so the View model only carries
// concrete values).
export type ViewShareRole = Exclude<ViewRole, symbol>;

export type ViewSharePrincipalType = Exclude<ViewPrincipalType, symbol>;

export const VIEW_SHARE_ROLE = {
  OWNER: 'OWNER',
  COLLABORATOR: 'COLLABORATOR',
  MEMBER: 'MEMBER',
} as const satisfies Record<string, ViewShareRole>;

export const VIEW_SHARE_PRINCIPAL_TYPE = {
  USER: 'USER',
  TEAM: 'TEAM',
} as const satisfies Record<string, ViewSharePrincipalType>;

export type ViewSharePermission = {
  principalId: number;
  principalType: ViewSharePrincipalType;
  role: ViewShareRole;
};

export type ViewColumn = {
  name: string;
};

export type ViewFilterWithNoValue = {
  property: string;
  operator: string;
  default?: boolean;
};

export type ViewFilterWithSingleValue = ViewFilterWithNoValue & {
  value: string | number;
};

export type ViewFilterWithMultipleValues = ViewFilterWithNoValue & {
  values: Array<string | number>;
};

export type ViewFilter =
  | ViewFilterWithNoValue
  | ViewFilterWithSingleValue
  | ViewFilterWithMultipleValues;

export type ViewFiltersArray = Array<ViewFilter>;

export type ViewFilterPropertiesArray = Array<ViewColumn>;

export type CrossObjectFilter = {
  objectTypeId: string;
  operator: string;
  combinedAssociationTypeId?: string;
  query?: string;
  customQueryProperties?: Array<string>;
  filterGroups: Array<{ filters: ViewFiltersArray }>;
};

export type ViewFilterGroup = {
  filters: ViewFiltersArray;
  crossObjectFilters?: Array<CrossObjectFilter>;
};

export type ViewFilterGroupsArray = Array<ViewFilterGroup>;

export type ViewState = {
  sortKey: string;
  sortColumnName: string;
  order: typeof ASC | typeof DESC;
  pipelineId?: string;
};

export const VisualizationEnum = {
  list: 'list',
  board: 'board',
  report: 'report',
  map: 'map',
  gantt: 'gantt',
  calendar: 'calendar',
} as const;

export type VisualizationType =
  (typeof VisualizationEnum)[keyof typeof VisualizationEnum];

export type View = {
  id: ViewIdType;
  name: string;
  objectTypeId: string;
  idLabel?: string;
  ownerId?: number;
  teamId?: ViewTeamIdType;
  private: ViewPrivateType;
  type: typeof STANDARD | typeof DEFAULT | typeof FORECAST;
  columns: Array<ViewColumn>;
  state: ViewState;
  namespaceName: ViewNamespaceType;
  filterGroups: ViewFilterGroupsArray;
  quickFilters: ViewFiltersArray;
  quickFilterProperties?: ViewFilterPropertiesArray;
  permissionDetails?: ViewPermissionDetailsType;
  permissions?: ViewSharePermission[];
  userRole?: ViewShareRole;
  currentViewVisualization?: string | null;
  viewColor?: string | null;
  publicDefaultRole?: number | null;
  lastUsedTimestamp?: number | null;
};

export type BackendView = Omit<
  View,
  | 'filterGroups'
  | 'quickFilters'
  | 'quickFilterProperties'
  | 'state'
  | 'columns'
  | 'type'
> & {
  idLabel?: string;
  filterGroups: string;
  quickFilters: string;
  quickFilterProperties?: string;
  state: string;
  columns: string;
  type?: View['type'];
};

export type SortOrder = 'ASC' | 'DESC';

export type ViewsSearchQueryType = {
  viewName?: string;
  ownerIds?: string[];
  teamIds?: string[];
  objectTypeIds?: string[];
  private?: boolean;
  limit?: number;
  pagingToken?: string;
  sortColumn?: string;
  sortOrder?: SortOrder;
  namespace: string;
  includeLastUsed?: boolean;
  lastUsedStart?: number;
  lastUsedEnd?: number;
  lastUsedPreset?: string;
};
